import { router } from "@inertiajs/react"
import { useState } from "react"

import { Input } from "@/components/ui/input"
import { rate } from "@/lib/money"
import type { TemplateDetail } from "@/types/bids"

/** The template's cost-per-SF roll-up: labor + setting materials + counted
 *  trims → the adder that bakes into every level's COGS. The sample-SF
 *  control re-evaluates count/min/ceil rules against a different lot size —
 *  a server round-trip, since $/SF is money. */
export function RollupCard({ template }: { template: TemplateDetail }) {
  const { rollup } = template.draft
  const [sample, setSample] = useState(template.sample_sf)
  const [label, setLabel] = useState(template.sample_label ?? "")

  const commitSample = () => {
    if (sample === template.sample_sf && (template.sample_label ?? "") === label.trim()) return
    router.patch(
      template.urls.update,
      { order_template: { sample_sf: sample, sample_label: label.trim() || null } },
      { preserveScroll: true }
    )
  }

  return (
    <section
      className="rounded-lg border border-hairline bg-surface p-4"
      data-testid="rollup-card"
    >
      <h2>{template.label} template — cost per SF</h2>
      <dl className="mt-3 grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
        <dt className="text-ink-muted">Labor</dt>
        <dd className="text-right tabular-nums">{rate(rollup.labor)}</dd>
        <dt className="text-ink-muted">Setting materials</dt>
        <dd className="text-right tabular-nums">{rate(rollup.materials)}</dd>
        <dt className="text-ink-muted">Trims (counted)</dt>
        <dd className="text-right tabular-nums">{rate(rollup.counts)}</dd>
        <dt className="border-t border-hairline pt-1.5">
          <strong>Template adder</strong>
        </dt>
        <dd className="border-t border-hairline pt-1.5 text-right tabular-nums">
          <strong data-testid="rollup-total">{rate(rollup.total, "SF")}</strong>
        </dd>
      </dl>
      <p className="mt-3 text-xs text-ink-muted">
        Baked into every {template.label} level&apos;s COGS in the bid workspace — product cost is
        the only thing that changes between levels.
      </p>

      <div className="mt-4 flex items-end gap-2 border-t border-hairline pt-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="sample-sf">Sample lot</label>
          <div className="flex items-center gap-2">
            <Input
              id="sample-sf"
              type="number"
              step="any"
              className="w-28"
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              onBlur={commitSample}
              onKeyDown={(e) => e.key === "Enter" && commitSample()}
              data-testid="sample-sf-input"
            />
            <span className="text-sm text-ink-muted">SF</span>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor="sample-label">Label</label>
          <Input
            id="sample-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={commitSample}
            placeholder="Lot 47"
            data-testid="sample-label-input"
          />
        </div>
      </div>
    </section>
  )
}
